import { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Layout } from '@/components/Layout';
import { useCustomer, useCustomerLeads, useCustomerJobs, useCustomerCommunications, useUpdateCustomer } from '@/hooks/useCustomers';
import { useCustomerActivity } from '@/hooks/useCustomerActivity';
import { useCustomerMetrics } from '@/hooks/useCustomerMetrics';
import { CustomerForm } from '@/components/customers/CustomerForm';
import { CustomerLeadsTable } from '@/components/customers/CustomerLeadsTable';
import { CustomerJobsTable } from '@/components/customers/CustomerJobsTable';
import { CustomerFinancials } from '@/components/customers/CustomerFinancials';
import { CustomerCommunications } from '@/components/customers/CustomerCommunications';
import { CustomerPortalAccessCard } from '@/components/customers/CustomerPortalAccessCard';
import { CustomerReviewRequestsCard } from '@/components/customers/CustomerReviewRequestsCard';
import { CustomerActivityTimeline } from '@/components/customers/CustomerActivityTimeline';
import { CustomerMetricsCard } from '@/components/customers/CustomerMetricsCard';
import { CustomerInvoicesTable } from '@/components/customers/CustomerInvoicesTable';
import { CustomerEstimatesTable } from '@/components/customers/CustomerEstimatesTable';
import { NewEstimateDialog } from '@/components/estimates/NewEstimateDialog';
import { useCustomerEstimates } from '@/hooks/useEstimates';
import { FileText } from 'lucide-react';
import { CustomerFormData } from '@/schemas/customer.schema';
import { Customer } from '@/types/customer';
import { useCustomerInvoices } from '@/hooks/useInvoices';
import { useVisibility } from '@/hooks/useVisibility';
import { useTenant } from '@/hooks/useTenant';
import { format } from 'date-fns';

const TABS = new Set(['overview', 'activity', 'leads', 'jobs', 'finance', 'estimates', 'invoices', 'communications']);

export default function CustomerDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { canSeeCompanyFinancials } = useVisibility();
  const { isAdmin, isSuperAdmin, isCSR, isLoadingMe } = useTenant();
  // Mirrors the customers_update RLS policy: admins always, CSRs for the
  // customers they created / are assigned to (the DB enforces that half).
  // Techs, affiliates and role-less users can't update rows, so their fields are read-only.
  const canEditProfile = !isLoadingMe && (isAdmin || isSuperAdmin || isCSR);

  // `?tab=` deep-links (bell notifications land on Communications)
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const [tab, setTab] = useState(tabParam && TABS.has(tabParam) ? tabParam : 'activity');
  useEffect(() => {
    if (tabParam && TABS.has(tabParam)) setTab(tabParam);
  }, [tabParam]);

  const { data: customer, isLoading: loadingCustomer } = useCustomer(id || null);
  const { data: leads = [], isLoading: loadingLeads } = useCustomerLeads(id || null);
  const { data: jobs = [], isLoading: loadingJobs } = useCustomerJobs(id || null);
  const { data: communications = [], isLoading: loadingComms } = useCustomerCommunications(id || null);
  const { data: activities = [] } = useCustomerActivity(id || null);
  const { data: metrics } = useCustomerMetrics(id || null);
  const { data: invoices = [], isLoading: loadingInvoices } = useCustomerInvoices(id || null);
  const { data: estimates = [], isLoading: loadingEstimates } = useCustomerEstimates(id || null);
  const [showNewEstimate, setShowNewEstimate] = useState(false);
  const updateCustomer = useUpdateCustomer();

  const handleSave = (data: CustomerFormData) => {
    if (!customer) return;
    // The form re-syncs itself once the saved row lands in the cache, so there
    // is nothing to reset here.
    updateCustomer.mutate({ id: customer.id, ...data });
  };

  if (loadingCustomer) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <p>Loading customer...</p>
        </div>
      </Layout>
    );
  }

  if (!customer) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <p>Customer not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        {/* Header with Health & Value Badges */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/customers')} className="mt-1">
              <ArrowLeft className="h-5 w-5" />
            </Button>

            <div className="space-y-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-3xl font-bold tracking-tight">
                  {customer.customer_type === 'company' && customer.company_name
                    ? customer.company_name
                    : customer.customer_name}
                </h1>

                {/* ID Badge */}
                {(customer as any).customer_number && (
                  <Badge variant="outline" className="font-mono text-xs text-muted-foreground">
                    {(customer as any).customer_number}
                  </Badge>
                )}

                {/* Loyalty Badge Logic */}
                {(() => {
                  const jobCount = jobs?.length || 0;
                  const ltv = customer.total_lifetime_value || 0;

                  if ((canSeeCompanyFinancials && ltv > 5000) || jobCount > 5) {
                    return <Badge variant="primary-soft">💎 VIP Customer</Badge>;
                  } else if (jobCount > 1) {
                    return <Badge variant="info">🔄 Returning</Badge>;
                  } else {
                    return <Badge variant="secondary">New Customer</Badge>;
                  }
                })()}
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-1">
                  <span className="font-medium text-foreground">Type:</span> {customer.customer_type}
                </div>

                {/* Lifetime Value */}
                {canSeeCompanyFinancials && (
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-foreground">LTV:</span>
                    <span className="text-success font-bold">
                      {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(customer.total_lifetime_value || 0)}
                    </span>
                  </div>
                )}

                {/* Engagement Pulse */}
                <div className="flex items-center gap-1">
                  <span className="font-medium text-foreground">Last Contact:</span>
                  {(() => {
                    if (!customer.last_contact_date) return <span>Never</span>;

                    const daysAgo = Math.floor((new Date().getTime() - new Date(customer.last_contact_date).getTime()) / (1000 * 60 * 60 * 24));
                    let colorClass = "text-muted-foreground";

                    if (daysAgo < 30) colorClass = "text-success font-medium";
                    else if (daysAgo < 90) colorClass = "text-warning";
                    else if (daysAgo > 180) colorClass = "text-destructive font-medium";

                    return (
                      <span className={colorClass}>
                        {format(new Date(customer.last_contact_date), "MMM d, yyyy")} ({daysAgo} days ago)
                      </span>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Enhanced Metrics Dashboard */}
        {metrics && <CustomerMetricsCard metrics={metrics} />}

        {/* Tabs - Timeline First */}
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="activity">Activity Timeline</TabsTrigger>
            <TabsTrigger value="leads">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="jobs">Jobs ({jobs.length})</TabsTrigger>
            <TabsTrigger value="finance">Finance</TabsTrigger>
            <TabsTrigger value="estimates">Estimates ({estimates.length})</TabsTrigger>
            <TabsTrigger value="invoices">Invoices ({invoices.length})</TabsTrigger>
            <TabsTrigger value="communications">Communications ({communications.length})</TabsTrigger>
          </TabsList>

          {/* forceMount keeps the profile form (and any half-typed edits) alive while
              other tabs are open; `hidden` still collapses it when not selected. */}
          <TabsContent value="overview" className="space-y-6" forceMount hidden={tab !== 'overview'}>
            <Card>
              <CardHeader>
                <CardTitle>Customer Information</CardTitle>
              </CardHeader>
              <CardContent>
                <CustomerForm
                  customer={customer as Customer}
                  onSubmit={handleSave}
                  canEdit={canEditProfile}
                  isSaving={updateCustomer.isPending}
                />
              </CardContent>
            </Card>
            <CustomerPortalAccessCard customer={{ id: customer.id, primary_email: customer.primary_email }} />
            <CustomerReviewRequestsCard
              customer={{ id: customer.id, review_requests_opted_out: (customer as { review_requests_opted_out?: boolean | null }).review_requests_opted_out }}
              canEdit={canEditProfile}
            />
          </TabsContent>

          <TabsContent value="activity">
            <CustomerActivityTimeline activities={activities} />
          </TabsContent>

          <TabsContent value="leads">
            {loadingLeads ? (
              <p>Loading leads...</p>
            ) : (
              <CustomerLeadsTable leads={leads} />
            )}
          </TabsContent>

          <TabsContent value="jobs">
            {loadingJobs ? (
              <p>Loading jobs...</p>
            ) : (
              <CustomerJobsTable jobs={jobs} />
            )}
          </TabsContent>

          <TabsContent value="finance">
            {loadingJobs ? (
              <p>Loading financials...</p>
            ) : (
              <CustomerFinancials
                jobs={jobs}
                totalLifetimeValue={customer.total_lifetime_value}
              />
            )}
          </TabsContent>

          <TabsContent value="estimates">
            <div className="mb-3 flex justify-end">
              <Button size="sm" onClick={() => setShowNewEstimate(true)} className="gap-2">
                <FileText className="h-4 w-4" />
                New Estimate
              </Button>
            </div>
            <CustomerEstimatesTable estimates={estimates} isLoading={loadingEstimates} />
            <NewEstimateDialog
              open={showNewEstimate}
              onOpenChange={setShowNewEstimate}
              customer={{
                id: customer.id,
                customer_name: customer.customer_name,
                primary_phone: customer.primary_phone,
                primary_email: customer.primary_email,
                primary_address: customer.primary_address,
              }}
            />
          </TabsContent>

          <TabsContent value="invoices">
            {loadingInvoices ? (
              <p>Loading invoices...</p>
            ) : (
              <CustomerInvoicesTable invoices={invoices} />
            )}
          </TabsContent>

          <TabsContent value="communications">
            {loadingComms ? (
              <p>Loading communications...</p>
            ) : (
              <CustomerCommunications
                customerId={customer.id}
                communications={communications}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
